import { useState } from 'react';

import { NumberField } from '@components/number-field';
import { useScopedTranslate } from '@locale/translate';

import { EditableCellPopover, FieldEditorBody } from './editable-cell-popover';
import { editableCellLocale } from './locale';
import { getNumberValueError, toEditableNumber } from './number-value';
import { EditableCellEditorProps } from './types';
import { useEditableCellTrigger } from './use-editable-cell-trigger';

import { typeRenderMap } from '../renderers';

// A decimal attribute can reach the table as a string - DRF renders Decimal
// that way - so the cell takes both and lets NumberField do the parsing.
type EditableNumberCellProps = EditableCellEditorProps<number | string | undefined>;

export const EditableNumberCell = ({ column, row, value }: EditableNumberCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const isDecimal = column.type === 'decimal';
  const [draft, setDraft] = useState<number | undefined>(() => toEditableNumber(value));

  const valueError = getNumberValueError(draft, { isDecimal });
  const validationError = valueError ? t(valueError) : undefined;

  // NumberField reports an emptied field as undefined, which is what clears the
  // attribute - the same empty string every other editor sends.
  const toPayload = (number: number | undefined) => (number === undefined ? '' : String(number));
  const payload = toPayload(draft);

  const trigger = getTrigger(value, column, row, () => typeRenderMap.number(value, column, row));

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      validationError={validationError}
      isValidationErrorInField
      onOpen={() => setDraft(toEditableNumber(value))}
      isSaveDisabled={payload === toPayload(toEditableNumber(value))}
      onSave={async () => {
        await column.onEdit?.(row, payload);
      }}
    >
      <FieldEditorBody>
        <NumberField
          label={column.title}
          defaultValue={value ?? null}
          isDecimal={isDecimal}
          error={validationError}
          onChange={setDraft}
          autoFocus
        />
      </FieldEditorBody>
    </EditableCellPopover>
  );
};
